import {
  KeyRoundIcon,
  PlugZapIcon,
  SearchXIcon,
  ShieldAlertIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import type { ComponentType } from 'react';

import { ApiError, ContractError } from '@/api/http';
import { StateBlock, type StateTone } from '@/components/StateBlock';
import { Button } from '@/components/ui/button';

interface Presentation {
  icon: ComponentType<{ className?: string }>;
  tone: StateTone;
  title: string;
  detail: string;
  retryable: boolean;
}

/**
 * Turn a thrown error into something a reader can act on.
 *
 * The rule this enforces is plan FR-DASH-11 applied to failures as well as
 * empties: state the reason. "Something went wrong. Retry." teaches the reader
 * nothing, and a retry button on a 403 is a lie.
 */
function present(error: unknown): Presentation {
  if (error instanceof ContractError) {
    return {
      icon: TriangleAlertIcon,
      tone: 'destructive',
      title: 'This screen and the API disagree',
      detail: `${error.message} Reloading will not help. This needs a dashboard deploy.`,
      retryable: false,
    };
  }

  if (error instanceof ApiError) {
    if (error.status === 0) {
      return {
        icon: PlugZapIcon,
        tone: 'warning',
        title: 'Cannot reach Zeligate',
        detail:
          'The request never left the browser or never got a reply. Check your connection, a VPN, or a content blocker.',
        retryable: true,
      };
    }
    if (error.status === 401) {
      return {
        icon: KeyRoundIcon,
        tone: 'warning',
        title: 'Your session has ended',
        detail: 'Sign in again to keep reading this project.',
        retryable: true,
      };
    }
    if (error.status === 403) {
      return {
        icon: ShieldAlertIcon,
        tone: 'warning',
        title: 'Your role does not allow this',
        detail:
          'You are a member of this organisation, but this action needs a higher role. An owner or admin can change that.',
        retryable: false,
      };
    }
    if (error.status === 404) {
      return {
        icon: SearchXIcon,
        tone: 'neutral',
        title: 'Not found',
        detail:
          'This resource does not exist, or it belongs to an organisation you are not a member of.',
        retryable: false,
      };
    }
    if (error.status === 429) {
      return {
        icon: TriangleAlertIcon,
        tone: 'warning',
        title: 'Too many requests',
        detail: 'The read API is rate limiting this project. Wait a moment and try again.',
        retryable: true,
      };
    }
    return {
      icon: TriangleAlertIcon,
      tone: 'destructive',
      title: `The API returned ${error.status}`,
      detail: error.requestId
        ? `${error.message} Request id ${error.requestId}.`
        : error.message,
      retryable: error.isRetryable,
    };
  }

  return {
    icon: TriangleAlertIcon,
    tone: 'destructive',
    title: 'This screen failed to load',
    detail: error instanceof Error ? error.message : String(error),
    retryable: true,
  };
}

export function ErrorState({
  error,
  onRetry,
  fill,
}: {
  error: unknown;
  onRetry?: () => void;
  fill?: boolean;
}) {
  const view = present(error);
  return (
    <StateBlock
      icon={view.icon}
      tone={view.tone}
      title={view.title}
      detail={view.detail}
      fill={fill}
      action={
        view.retryable && onRetry ? (
          <Button size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  );
}
